import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useStore } from '../data/store';
import { Users, Trophy, Settings, Search, Filter } from 'lucide-react';

const Players = () => {
  const { players = [] } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');

  const allPositions = Array.from(
    new Set(
      players
        .filter(p => p.role === 'player')
        .flatMap(p => Array.isArray(p.positions) ? p.positions : [p.positions])
        .filter(Boolean)
    )
  ).sort();

  const teamPlayers = players
    .filter(p => p.role === 'player')
    .filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           p.number?.toString().includes(searchTerm);
      const matchesPosition = positionFilter === 'all' ||
                             (Array.isArray(p.positions)
                               ? p.positions.includes(positionFilter)
                               : p.positions === positionFilter);
      return matchesSearch && matchesPosition;
    })
    .sort((a, b) => {
      const numA = parseInt(a.number);
      const numB = parseInt(b.number);
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;
      return numA - numB;
    });

  const gamedayOperators = players
    .filter(p => p.role === 'gameday')
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Users className="w-8 h-8 text-[#8B0000]" />
            <h1 className="text-4xl font-bold text-white">Our Team</h1>
          </div>

          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-[#222222] text-white rounded-lg border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50 transition-colors"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                className="w-full sm:w-48 pl-10 pr-4 py-3 bg-[#222222] text-white rounded-lg border border-gray-700 focus:border-[#8B0000] focus:ring focus:ring-[#8B0000]/50 transition-colors appearance-none cursor-pointer"
              >
                <option value="all">All Positions</option>
                {allPositions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Players Section */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Trophy className="w-6 h-6 text-cardinal" />
                <h2 className="text-2xl font-bold text-white">Players</h2>
              </div>
              <span className="text-gray-400">
                {teamPlayers.length} player{teamPlayers.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamPlayers.map((player) => (
                <Link
                  key={player.id}
                  to={`/players/${player.id}`}
                  className="bg-[#222222] rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform border border-white/10 group"
                >
                  <div className="relative">
                    <img 
                      src={player.image || '/default-player.png'}
                      alt={player.name || 'Unknown Player'} 
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <div className="flex justify-between items-end">
                        <div>
                          <h2 className="text-2xl font-bold text-white group-hover:text-cardinal transition-colors">
                            {player.name || 'Unknown'}
                          </h2>
                          <div className="flex flex-wrap items-center gap-2 text-gray-300">
                            <Trophy className="w-4 h-4" />
                            <span>
                              {Array.isArray(player.positions) ? player.positions.join(' / ') : player.positions || "No position listed"}
                            </span>
                          </div>
                        </div>
                        <span className="text-3xl font-bold text-cardinal">
                          {player.number ? `#${player.number}` : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Operators Section */}
          <section>
            <div className="flex items-center gap-3 mb-8">
              <Settings className="w-6 h-6 text-cardinal" />
              <h2 className="text-2xl font-bold text-white">Operators</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gamedayOperators.map((operator) => (
                <Link
                  key={operator.id}
                  to={`/players/${operator.id}`}
                  className="bg-[#222222] rounded-lg overflow-hidden shadow-lg transform hover:scale-105 transition-transform border border-white/10 group"
                >
                  <div className="relative">
                    <img 
                      src={operator.image || '/default-operator.png'}
                      alt={operator.name || 'Unknown Operator'} 
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <div className="flex justify-between items-end">
                        <div>
                          <h2 className="text-2xl font-bold text-white group-hover:text-cardinal transition-colors">
                            {operator.name || 'Unknown'}
                          </h2>
                          <div className="flex flex-wrap items-center gap-2 text-gray-300">
                            <Settings className="w-4 h-4" />
                            <span>{operator.title || 'Gameday Staff'}</span>
                          </div>
                        </div>
                        {operator.number && (
                          <span className="text-3xl font-bold text-cardinal">
                            #{operator.number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}

              {gamedayOperators.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Settings className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No operators have been added yet.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Players;